import { useEffect, useMemo, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  getManufacturingOrgSerialConvention,
  upsertManufacturingOrgSerialConvention,
} from "@/lib/manufacturing/orgSerialConventionClient";
import {
  decodeSkuSerial,
  DEFAULT_SKU_SERIAL_CONVENTION,
  formatSkuSerial,
  SERIAL_CONVENTION_TOKEN_HELP,
  type SkuSerialConvention,
} from "@/lib/manufacturing/skuSerialConvention";

export function ManufacturingOrgSerialConventionCard() {
  const [settings, setSettings] = useState<SkuSerialConvention>({
    ...DEFAULT_SKU_SERIAL_CONVENTION,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [samplePackage, setSamplePackage] = useState("DP");
  const [incoming, setIncoming] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await getManufacturingOrgSerialConvention();
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setSettings(res.data.settings);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const preview = useMemo(
    () =>
      formatSkuSerial(settings, {
        sizeLabel: "10x12",
        groupCode: "CL",
        seriesName: "Classic",
        styleKey: "Utility",
        packageCode: samplePackage || settings.default_package,
        serialNumber: null,
        productionDate: null,
      }),
    [settings, samplePackage],
  );

  const previewFilled = useMemo(
    () =>
      formatSkuSerial(settings, {
        sizeLabel: "10x12",
        groupCode: "CL",
        seriesName: "Classic",
        styleKey: "Utility",
        packageCode: samplePackage || settings.default_package,
        serialNumber: 1,
        productionDate: new Date(2026, 6, 31),
      }),
    [settings, samplePackage],
  );
  const decoded = useMemo(() => decodeSkuSerial(settings.pattern, incoming), [settings.pattern, incoming]);
  const extraParts = decoded.parts.filter((part) => part.token !== "SERIAL" && !part.token.startsWith("SIZE"));

  async function save() {
    setBusy(true);
    setError(null);
    setSavedNote(null);
    try {
      const res = await upsertManufacturingOrgSerialConvention(settings);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setSettings(res.data.settings);
      setSavedNote("Saved org serial convention.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <SectionCard title="SKU serial convention">
      <p className="mb-3 text-xs text-slate-600 dark:text-slate-400">
        Org-wide unit serial pattern. Serial digits and production date are placeholders until the
        production pipeline issues them. Apply{" "}
        <span className="font-mono">docs/sql/mfg_org_serial_conventions_v1.sql</span> once per
        environment.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-sm sm:col-span-2">
          <span className="text-slate-600 dark:text-slate-300">Pattern</span>
          <input
            className={`${inputClass} mt-1 font-mono text-xs`}
            value={settings.pattern}
            onChange={(e) => setSettings((s) => ({ ...s, pattern: e.target.value }))}
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Serial digits</span>
          <input
            className={`${inputClass} mt-1`}
            type="number"
            min={1}
            max={12}
            value={settings.serial_digits}
            onChange={(e) =>
              setSettings((s) => ({
                ...s,
                serial_digits: Number(e.target.value) || 5,
              }))
            }
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Date format</span>
          <input
            className={`${inputClass} mt-1 font-mono text-xs`}
            value={settings.date_format}
            onChange={(e) => setSettings((s) => ({ ...s, date_format: e.target.value }))}
            placeholder="ddmmyy"
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Default package (no package)</span>
          <input
            className={`${inputClass} mt-1 font-mono text-xs`}
            value={settings.default_package}
            onChange={(e) => setSettings((s) => ({ ...s, default_package: e.target.value }))}
            placeholder="NP"
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Preview package</span>
          <input
            className={`${inputClass} mt-1 font-mono text-xs`}
            value={samplePackage}
            onChange={(e) => setSamplePackage(e.target.value.toUpperCase())}
            placeholder="DP"
          />
        </label>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">{SERIAL_CONVENTION_TOKEN_HELP}</p>
      <div className="mt-2 rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-xs dark:border-zinc-700 dark:bg-zinc-900">
        <div className="text-[10px] uppercase tracking-wide text-slate-500">Placeholder preview</div>
        <div>{preview}</div>
        <div className="mt-1 text-[10px] uppercase tracking-wide text-slate-500">
          Sample filled (serial 1 · 31 Jul 2026)
        </div>
        <div>{previewFilled}</div>
      </div>
      <label className="mt-3 block text-sm">
        <span className="text-slate-600 dark:text-slate-300">Decode an incoming serial</span>
        <input
          className={`${inputClass} mt-1 font-mono text-xs`}
          value={incoming}
          onChange={(e) => setIncoming(e.target.value)}
          placeholder="CL-UT-WP-1428-033126-056382"
          aria-label="Incoming serial"
        />
      </label>
      {incoming.trim() ? (
        <div className="mt-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs dark:border-zinc-700 dark:bg-zinc-900">
          {decoded.matched ? (
            <>
              <div>Size {decoded.size || "—"}</div>
              <div>Short serial {decoded.serial || "—"}</div>
              {extraParts.length > 0 ? (
                <div className="mt-1 text-slate-500">
                  {extraParts.map((part) => `${part.token} ${part.value}`).join(" · ")}
                </div>
              ) : null}
            </>
          ) : (
            <div className="text-slate-600 dark:text-slate-300">
              This serial does not line up with the pattern above.
            </div>
          )}
        </div>
      ) : null}
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
      {savedNote ? <p className="mt-2 text-xs text-emerald-700">{savedNote}</p> : null}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={mfgPrimaryBtnClass} disabled={busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save convention"}
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={busy}
          onClick={() => setSettings({ ...DEFAULT_SKU_SERIAL_CONVENTION })}
        >
          Reset defaults
        </button>
      </div>
    </SectionCard>
  );
}
